// frontend/src/utils/axiosConfig.js

import axios from 'axios';
import {
  showSessionExpiredToast,
  extractBackendMessage,
  getFriendlyErrorMessage,
} from './toastUtils';
import { resetSuppression } from './toastUtils'; // if you want to clear suppression on success

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const axiosInstance = axios.create({
  baseURL: API_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 120000,
  decompress: false,
  transitional: { clarifyTimeoutError: true },
});

const getToken = () =>
  localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');

const getRefreshToken = () =>
  localStorage.getItem('refreshToken') || sessionStorage.getItem('refreshToken');

const clearAllStorages = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  localStorage.removeItem('user');
  localStorage.removeItem('auth-storage');
  localStorage.removeItem('community-storage');

  sessionStorage.removeItem('accessToken');
  sessionStorage.removeItem('refreshToken');
  sessionStorage.removeItem('user');
};

const SESSION_EXPIRED_REDIRECT_DELAY_MS = 1800;
let logoutTimer = null;

const logout = (delayMs = 0) => {
  if (logoutTimer) {
    clearTimeout(logoutTimer);
    logoutTimer = null;
  }

  const doLogout = () => {
    clearAllStorages();
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login';
    }
  };

  if (delayMs > 0) {
    logoutTimer = setTimeout(doLogout, delayMs);
  } else {
    doLogout();
  }
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) prom.reject(error);
    else prom.resolve(token);
  });
  failedQueue = [];
};

// ─────────────────────────────────────────────────────────────
// Attach a friendly message to the error for the caller to use
// ─────────────────────────────────────────────────────────────
const decorateError = (error) => {
  if (!error || typeof error !== 'object') return error;

  // Extract the backend's friendly message
  const backendMessage = extractBackendMessage(error);
  if (backendMessage) {
    error.friendlyMessage = backendMessage;
  } else {
    const status = error.response?.status;
    const fallback = getFriendlyErrorMessage(status);
    if (fallback) error.friendlyMessage = fallback;
  }

  return error;
};

axiosInstance.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;

    if (config.url?.includes('/messages/')) {
      config.headers['Cache-Control'] = 'no-cache';
      config.headers['Pragma'] = 'no-cache';
    }

    return config;
  },
  (error) => Promise.reject(decorateError(error))
);

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    // ----- Network-level errors -----
    if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
      error.friendlyMessage = 'No internet connection. Please check your network and try again.';
      return Promise.reject(error);
    }

    if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
      error.friendlyMessage = 'The request took too long. Please try again.';
      return Promise.reject(error);
    }

    if (error.code === 'ERR_CONTENT_DECODING_FAILED') {
      if (error.config && !error.config._retryDecode) {
        error.config._retryDecode = true;
        error.config.headers['Accept-Encoding'] = 'identity';
        return axiosInstance(error.config);
      }
      error.friendlyMessage = 'We couldn\'t process the server response. Please try again.';
      return Promise.reject(error);
    }

    const originalRequest = error.config;
    const status = error.response?.status;

    const isLogout = originalRequest?.url?.includes('/auth/logout');
    const isAuthRefresh = originalRequest?.url?.includes('/auth/refresh');

    // ----- 401: try to refresh -----
    if (status === 401 && !originalRequest?._retry && !originalRequest?.url?.includes('/auth/')) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          })
          .catch((err) => Promise.reject(decorateError(err)));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        isRefreshing = false;
        processQueue(new Error('No refresh token'), null);
        showSessionExpiredToast('Your session has expired. Please log in again.');
        logout(SESSION_EXPIRED_REDIRECT_DELAY_MS);
        return Promise.reject(decorateError(error));
      }

      try {
        const response = await axios.post(`${API_URL}/auth/refresh-token`, { refreshToken });
        const { accessToken } = response.data.data;

        if (localStorage.getItem('refreshToken')) {
          localStorage.setItem('accessToken', accessToken);
        } else if (sessionStorage.getItem('refreshToken')) {
          sessionStorage.setItem('accessToken', accessToken);
        }

        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;

        processQueue(null, accessToken);
        isRefreshing = false;

        return axiosInstance(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        isRefreshing = false;
        showSessionExpiredToast('Your session has expired. Please log in again.');
        logout(SESSION_EXPIRED_REDIRECT_DELAY_MS);
        return Promise.reject(decorateError(refreshError));
      }
    }

    // ----- No toasts from here! Just decorate and reject -----
    return Promise.reject(decorateError(error));
  }
);

export default axiosInstance;